import { resolveWorkshopMessage } from "./workshop.js";

const LENGTH_PARAMS = new Set([
  "position",
  "length",
  "width",
  "from",
  "to",
  "offcutLength",
  "offcutWidth",
  "minSize",
]);

/**
 * @param {unknown} value
 * @returns {boolean}
 */
function isMessageDescriptor(value) {
  return Boolean(value && typeof value === "object" && "key" in value);
}

/**
 * @param {unknown} value
 * @returns {boolean}
 */
function isLiteralMessage(value) {
  return Boolean(value && typeof value === "object" && "literal" in value);
}

/**
 * @param {ReturnType<import("./createDisplay.js").createDisplay>} display
 * @param {Record<string, unknown>} params
 */
function formatCoreParams(display, params = {}) {
  const formatted = { ...params };

  if (typeof formatted.axisKey === "string") {
    formatted.axis = display.t(`core.strip.crossesPanel.${formatted.axisKey}`);
    delete formatted.axisKey;
  }

  if (formatted.panelName == null && "panelName" in formatted) {
    formatted.panelName = display.t("core.strip.crossesPanel.unknownPart");
  }

  if (Array.isArray(formatted.offcuts)) {
    formatted.offcutSizes = formatted.offcuts
      .map((offcut) => display.formatPanelSize(offcut.length, offcut.width))
      .join(", ");
    delete formatted.offcuts;
  }

  if (typeof formatted.offcutLength === "number" && typeof formatted.offcutWidth === "number") {
    formatted.size = display.formatPanelSize(formatted.offcutLength, formatted.offcutWidth);
    delete formatted.offcutLength;
    delete formatted.offcutWidth;
  }

  if (isMessageDescriptor(formatted.offcutLabel)) {
    formatted.offcutLabel = resolveCoreMessage(display, formatted.offcutLabel);
  }

  if (typeof formatted.modeKey === "string") {
    formatted.mode = display.t(`core.cutSummary.mode.${formatted.modeKey}`);
    delete formatted.modeKey;
  }

  if (Array.isArray(formatted.parts)) {
    formatted.parts = formatted.parts
      .map((part) => resolveCoreMessage(display, part))
      .filter(Boolean)
      .join("; ");
  }

  if (Array.isArray(formatted.sides)) {
    formatted.sides = formatted.sides
      .map((sideKey) => display.t(sideKey))
      .join(", ");
    if (formatted.sides.length === 1) {
      formatted.side = formatted.sides;
      delete formatted.sides;
    }
  }

  if (isMessageDescriptor(formatted.caption)) {
    formatted.caption = resolveWorkshopMessage(display, formatted.caption);
  }

  if (formatted.emphasizeExact) {
    formatted.prefix = display.t("core.sheetInstruction.exactPrefix");
  } else {
    formatted.prefix = "";
  }
  delete formatted.emphasizeExact;

  for (const [key, value] of Object.entries(formatted)) {
    if (typeof value === "number" && LENGTH_PARAMS.has(key)) {
      formatted[key] = display.formatLength(value);
    }
    if (typeof value === "number" && key === "thickness") {
      formatted[key] = display.formatThickness(value);
    }
  }

  return formatted;
}

/**
 * @param {ReturnType<import("./createDisplay.js").createDisplay>} display
 * @param {{ key: string, params?: Record<string, unknown> }|{ literal: string }|string|null|undefined} descriptor
 */
export function resolveCoreMessage(display, descriptor) {
  if (descriptor == null) {
    return null;
  }

  if (typeof descriptor === "string") {
    return descriptor;
  }

  if (isLiteralMessage(descriptor)) {
    return descriptor.literal;
  }

  if (!isMessageDescriptor(descriptor)) {
    return String(descriptor);
  }

  if (descriptor.key === "core.strip.crossesPanel") {
    const params = formatCoreParams(display, descriptor.params);
    return display.t("core.strip.crossesPanel.message", {
      axis: params.axis,
      position: params.position,
      panelName: params.panelName,
      body: display.t("core.strip.whenBlocked.body"),
    });
  }

  return display.t(descriptor.key, formatCoreParams(display, descriptor.params));
}

/**
 * @param {ReturnType<import("./createDisplay.js").createDisplay>} display
 * @param {{ title: object, detail?: object|null }} instruction
 */
export function resolveCorePreviewInstruction(display, instruction) {
  const title = resolveCoreMessage(display, instruction.title) ?? "";
  const detail = instruction.detail
    ? resolveCoreMessage(display, instruction.detail)
    : null;

  return { title, detail };
}

/**
 * @param {ReturnType<import("./createDisplay.js").createDisplay>} display
 * @param {object[]} headers
 */
export function resolveCutListCsvHeaders(display, headers) {
  return headers.map((header) => resolveCoreMessage(display, header));
}

/**
 * @param {ReturnType<import("./createDisplay.js").createDisplay>} display
 * @param {string} strategyId
 */
export function resolveCutStrategyLabel(display, strategyId) {
  return display.t(`core.strategy.${strategyId}.label`);
}

/**
 * @param {ReturnType<import("./createDisplay.js").createDisplay>} display
 * @param {string} strategyId
 */
export function resolveCutStrategyDescription(display, strategyId) {
  return display.t(`core.strategy.${strategyId}.description`);
}
